import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CloseOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import type { ISurveyCommittedEmissionsRemovalPreview } from '../../../shared/services/survey/survey.model';

type Props = {
  open: boolean;
  surveyId: string | null;
  hasActiveExtrapolation?: boolean;
  onClose: () => void;
  onRemoved: () => void;
  onNotify: (message: string, isError?: boolean) => void;
};

export const SurveyRemoveCommittedEmissionsDialog = ({
  open,
  surveyId,
  hasActiveExtrapolation = false,
  onClose,
  onRemoved,
  onNotify,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [preview, setPreview] = useState<ISurveyCommittedEmissionsRemovalPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !surveyId) {
      setPreview(null);
      setError(null);
      setLoading(false);
      setRemoving(false);
      return;
    }
    let cancelled = false;
    const svc = SurveyAdminService.getInstance();
    setLoading(true);
    setError(null);
    setPreview(null);
    svc
      .getSurveyCommittedEmissionsRemovalPreview(surveyId)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err: { response?: { data?: { message?: string } } }) => {
        if (!cancelled) {
          setError(err?.response?.data?.message ?? 'Could not load removal preview.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, surveyId]);

  const linked = preview?.linkedCalculationCount ?? 0;
  const entire = preview?.entireEmissionsRemovedCount ?? 0;
  const partial = preview?.emissionsRetainedPartialCount ?? 0;
  const canRemove = Boolean(surveyId) && linked > 0 && !error && !loading;

  const handleRemove = async () => {
    if (!surveyId || !canRemove) return;
    setRemoving(true);
    try {
      const svc = SurveyAdminService.getInstance();
      const result = await svc.removeSurveyCommittedEmissions(surveyId);
      const msg =
        result.deletedCalculationCount > 0
          ? `Removed ${result.deletedCalculationCount} calculation row(s)` +
              (result.deletedEntireEmissionCount > 0
                ? ` and ${result.deletedEntireEmissionCount} emission record(s).`
                : '.') +
              (result.sessionsMarkedUncommitted > 0
                ? ` ${result.sessionsMarkedUncommitted} response session${result.sessionsMarkedUncommitted === 1 ? '' : 's'} marked as not committed.`
                : '')
          : result.message;
      onNotify(msg);
      onRemoved();
      onClose();
    } catch (err: unknown) {
      const msg =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
          ? (err as { response: { data: { message: string } } }).response.data.message
          : 'Remove failed.';
      onNotify(msg, true);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={removing ? undefined : onClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='remove-survey-emissions-title'
    >
      <DialogTitle id='remove-survey-emissions-title' sx={{ pr: 5 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6' component='span' fontWeight={600}>
            Remove committed emissions from this survey
          </Typography>
          <IconButton onClick={() => !removing && onClose()} aria-label='Close' disabled={removing} sx={{ mr: -0.75 }}>
            <CloseOutlined />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          This deletes activity rows that were created when responses were committed to the report period. Only calculations
          linked to this survey (via the emission record linkage) are removed. Emissions that also have unrelated activity
          are kept: only survey-linked calculations are stripped.
        </Typography>
        {hasActiveExtrapolation && (
          <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
            <Stack direction='row' spacing={1} alignItems='flex-start'>
              <WarningAmberOutlined sx={{ color: 'warning.main', mt: 0.1, flexShrink: 0 }} fontSize='small' />
              <Typography variant='body2'>
                This survey has an active extrapolation. Removing emissions will also reverse the extrapolation and delete all extrapolated emission records.
              </Typography>
            </Stack>
          </Box>
        )}
        {loading && (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner />
          </Box>
        )}
        {error && !loading && <Typography color='error.main' variant='body2'>{error}</Typography>}
        {!loading && !error && linked === 0 && (
          <Typography color='text.secondary' variant='body2'>
            Nothing to remove: there are no survey-linked emission calculations in your organisation for this survey.
          </Typography>
        )}
        {!loading && !error && linked > 0 && (
          <Stack spacing={1}>
            <Typography variant='body1'>
              <strong>{linked}</strong> emission calculation row{linked === 1 ? '' : 's'} will be deleted.
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              <strong>{entire}</strong> emission record{entire === 1 ? '' : 's'} will be removed entirely
              {' '}(no other calculations remain).
            </Typography>
            {partial > 0 && (
              <Typography variant='body2' color='text.secondary'>
                <strong>{partial}</strong> emission record{partial === 1 ? '' : 's'} will remain with other activity data after
                survey-linked calculations are removed.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => !removing && onClose()} disabled={removing}>
          Cancel
        </Button>
        <LoadingButton
          color='error'
          variant='contained'
          loading={removing}
          disabled={!canRemove}
          onClick={handleRemove}
        >
          Remove emissions
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
